// File: components/Hires.js
import React, {useState, useEffect} from 'react';
import HireCostSummary from './HireCostSummary';
import HireMultiPeriodConfig from './HireMultiPeriodConfig';

const Hires = ({
                   hireDesigner,
                   setHireDesigner,
                   designerSalary,
                   setDesignerSalary,
                   designerEquity,
                   setDesignerEquity,
                   designerPeriods,
                   setDesignerPeriods,
                   designerEmploymentType,
                   setDesignerEmploymentType,

                   eaType,
                   setEaType,
                   eaSalary,
                   setEaSalary,
                   eaEquity,
                   setEaEquity,
                   eaPeriods,
                   setEaPeriods,
                   eaEmploymentType,
                   setEaEmploymentType,

                   hireSocial,
                   setHireSocial,
                   socialSalary,
                   setSocialSalary,
                   socialEquity,
                   setSocialEquity,
                   socialPeriods,
                   setSocialPeriods,
                   socialEmploymentType,
                   setSocialEmploymentType,

                   formatCurrency,
                   hiresConfig,
                   hiresInfo,
                   location,
                   applyLocationMultiplier,

                   // Localization configs
                   hireConfigPeriodInfo,
                   multiPeriodConfigInfo,
                   hireCostSummaryInfo,
                   summaryBoxInfo
               }) => {
    const [showDesignerInfo, setShowDesignerInfo] = useState(false);
    const [showEAInfo, setShowEAInfo] = useState(false);
    const [showSocialInfo, setShowSocialInfo] = useState(false);

    // Helper functions for calculating effective salary and equity based on periods
    const calculateEffectiveSalaryForPeriods = (baseSalary, periods, employmentType) => {
        // Ensure periods is an array
        const validPeriods = Array.isArray(periods) ? periods : [];
        if (validPeriods.length === 0) {
            // Default to full year, full time if no periods
            return baseSalary;
        }

        let totalSalary = 0;

        validPeriods.forEach(period => {
            // Calculate number of months in this period
            const monthsInPeriod = period.endMonth - period.startMonth;
            if (monthsInPeriod <= 0) return;

            // Calculate fraction of the year
            const yearFraction = monthsInPeriod / 12;

            // Calculate salary for this period based on hours
            let periodSalary = baseSalary * (period.hoursPerWeek / 40) * yearFraction;

            totalSalary += periodSalary;
        });

        // Apply contractor premium at the root level if applicable
        if (employmentType === 'contractor') {
            totalSalary *= 1.5;
        }

        return totalSalary;
    };

    const calculateEffectiveEquityForPeriods = (baseEquity, periods, employmentType) => {
        // No equity for contractors (handled at root level)
        if (employmentType === 'contractor') {
            return 0;
        }

        // Ensure periods is an array
        const validPeriods = Array.isArray(periods) ? periods : [];
        if (validPeriods.length === 0) {
            // Default to full equity if no periods defined
            return baseEquity;
        }

        // For equity calculation, we now only need to:
        // 1. Weight by time spent in each period (months)
        // 2. Weight by hours worked in each period

        let totalMonths = 0;
        let weightedEquity = 0;

        validPeriods.forEach(period => {
            // Calculate months in this period
            const monthsInPeriod = period.endMonth - period.startMonth;
            if (monthsInPeriod <= 0) return;

            // Calculate weighted equity for this period
            const periodEquity = baseEquity * (period.hoursPerWeek / 40) * (monthsInPeriod / 12);

            weightedEquity += periodEquity;
            totalMonths += monthsInPeriod;
        });

        return weightedEquity;
    };

    // Configuration for HireMultiPeriodConfig
    const hireOptionsConfig = {
        multiPeriodTitleLabel: "Work Configuration Timeline",
        hoursPerWeekLabel: "Hours per week",
        employmentTypeLabel: "Employment Type",
        employeeLabel: "Employee",
        contractorLabel: "Contractor",
        periodStartLabel: "Start Month",
        periodEndLabel: "End Month",
        immediateHireLabel: "Start (Month 0)",
        yearEndLabel: "End of Year (Month 12)",
        monthLabel: "Month",
        monthsLabel: "Months",
        fullYearLabel: "Full Year",
        followedByLabel: "Followed by →",
        addPeriodLabel: "+ Add Another Period",
        removeLabel: "Remove Period",
        maxPeriodsNoteLabel: "({current}/{max} periods)",
        periodHelpText: "Define different working arrangements throughout the year with multiple periods."
    };

    // Effect to update equity when salary changes
    useEffect(() => {
        if (hireDesigner) {
            // Calculate based on salary and adjust for periods
            const baseEquity = hiresConfig.designer.equityCalculation.formula(designerSalary);
            // Equity should be calculated with periods in mind, but type is at root level
            const adjustedEquity = calculateEffectiveEquityForPeriods(baseEquity, designerPeriods, designerEmploymentType);
            setDesignerEquity(parseFloat(adjustedEquity.toFixed(2)));
        }
    }, [hireDesigner, designerSalary, designerPeriods, designerEmploymentType]);

    // Effect for EA equity calculation
    useEffect(() => {
        if (eaType === 'regular') {
            const baseEquity = hiresConfig.ea.types.regular.equityCalculation.formula(eaSalary);
            const adjustedEquity = calculateEffectiveEquityForPeriods(baseEquity, eaPeriods, eaEmploymentType);
            setEaEquity(parseFloat(adjustedEquity.toFixed(2)));
        } else if (eaType === 'clinical') {
            const baseEquity = hiresConfig.ea.types.clinical.equityCalculation.formula(eaSalary);
            const adjustedEquity = calculateEffectiveEquityForPeriods(baseEquity, eaPeriods, eaEmploymentType);
            setEaEquity(parseFloat(adjustedEquity.toFixed(2)));
        }
    }, [eaType, eaSalary, eaPeriods, eaEmploymentType]);

    // Effect for Social Media Manager equity calculation
    useEffect(() => {
        if (hireSocial) {
            const baseEquity = hiresConfig.social.equityCalculation.formula(socialSalary);
            const adjustedEquity = calculateEffectiveEquityForPeriods(baseEquity, socialPeriods, socialEmploymentType);
            setSocialEquity(parseFloat(adjustedEquity.toFixed(2)));
        }
    }, [hireSocial, socialSalary, socialPeriods, socialEmploymentType]);

    // Calculate the percentage of full-time equivalent across all periods
    const calculateFtePercentage = (periods) => {
        // Ensure periods is an array
        const validPeriods = Array.isArray(periods) ? periods : [];
        if (validPeriods.length === 0) return 100;

        let totalMonths = 0;
        let weightedHours = 0;

        validPeriods.forEach(period => {
            const monthsInPeriod = period.endMonth - period.startMonth;
            if (monthsInPeriod <= 0) return;

            weightedHours += period.hoursPerWeek * monthsInPeriod;
            totalMonths += monthsInPeriod;
        });

        // Calculate average weekly hours across the year, weighted by months
        const avgHoursPerWeek = totalMonths > 0 ? weightedHours / totalMonths : 40;

        // Calculate percentage of full-time (40 hours)
        return Math.round((avgHoursPerWeek / 40) * 100);
    };

    // Calculate estimated average hours per week across all periods
    const calculateAverageHours = (periods) => {
        // Ensure periods is an array
        const validPeriods = Array.isArray(periods) ? periods : [];
        if (validPeriods.length === 0) return 40;

        let totalMonths = 0;
        let weightedHours = 0;

        validPeriods.forEach(period => {
            const monthsInPeriod = period.endMonth - period.startMonth;
            if (monthsInPeriod <= 0) return;

            weightedHours += period.hoursPerWeek * monthsInPeriod;
            totalMonths += monthsInPeriod;
        });

        // Calculate average weekly hours across the year, weighted by months
        return totalMonths > 0 ? Math.round(weightedHours / totalMonths) : 40;
    };

    // Helper function to display the multi-period configuration in a human-readable format
    const displayWorkConfiguration = (periods) => {
        // Ensure periods is an array
        const validPeriods = Array.isArray(periods) ? periods : [];
        if (validPeriods.length === 0) return hiresInfo.fullTimeEntireYearText || "Full-time, entire year";
        if (validPeriods.length === 1) {
            const period = periods[0];
            if (period.startMonth === 0 && period.endMonth === 12 && period.hoursPerWeek === 40) {
                return hiresInfo.fullTimeEntireYearText || "Full-time, entire year";
            } else if (period.startMonth === 0 && period.endMonth === 12) {
                return `${period.hoursPerWeek} ${hiresInfo.hrsWeekText || "hrs/week"} (${calculateFtePercentage([period])}% ${hiresInfo.fteText || "FTE"}), ${hiresInfo.entireYearText || "entire year"}`;
            } else {
                const monthsInPeriod = period.endMonth - period.startMonth;
                return `${period.hoursPerWeek} ${hiresInfo.hrsWeekText || "hrs/week"}, ${hiresInfo.monthsText || "months"} ${period.startMonth}-${period.endMonth} (${monthsInPeriod} ${hiresInfo.monthsText || "months"})`;
            }
        } else {
            // Format for multiple periods
            const avgHours = calculateAverageHours(periods);
            const ftePercentage = calculateFtePercentage(periods);
            return `${hiresInfo.multiPeriodText || "Multi-period"}: ~${avgHours} ${hiresInfo.hrsWeekText || "hrs/week"} ${hiresInfo.avgText || "avg"} (${ftePercentage}% ${hiresInfo.fteText || "FTE"}), ${periods.length} ${hiresInfo.periodsText || "periods"}`;
        }
    };

    // Helper function to display salary with consideration for multi-period configuration
    const displaySalary = (salary, periods, employmentType) => {
        const effectiveSalary = calculateEffectiveSalaryForPeriods(salary, periods, employmentType);
        // Just show the appropriate salary for the current location
        const adjustedSalary = applyLocationMultiplier ? applyLocationMultiplier(effectiveSalary) : effectiveSalary;
        return formatCurrency(adjustedSalary);
    };

    // Helper function to display equity with periods considered
    const displayEquity = (baseEquity, periods, employmentType) => {
        if (employmentType === 'contractor') {
            return '0.00%';
        }
        const effectiveEquity = calculateEffectiveEquityForPeriods(baseEquity, periods, employmentType);
        return effectiveEquity.toFixed(2) + '%';
    };

    // Function to update designer periods and recalculate
    const updateDesignerPeriods = (newPeriods, hoursChanged = false) => {
        setDesignerPeriods(newPeriods);

        // If this is a hours change on the primary period, adjust the salary proportionally
        if (hoursChanged && newPeriods.length > 0 && hireDesigner) {
            const firstPeriod = newPeriods[0];
            // Get the ratio of new hours to full time (40 hours)
            const hourRatio = firstPeriod.hoursPerWeek / 40;

            // Scale the salary proportionally to the hours
            // First check if salary is at or above the max
            const currentMaxSalary = hiresConfig.designer.maxSalary * hourRatio;
            if (designerSalary > currentMaxSalary) {
                // If salary would be above the new scaled max, adjust it down
                setDesignerSalary(Math.round(currentMaxSalary));
            }
        }

        // Recalculate equity based on new periods
        if (hireDesigner) {
            const baseEquity = hiresConfig.designer.equityCalculation.formula(designerSalary);
            const adjustedEquity = calculateEffectiveEquityForPeriods(baseEquity, newPeriods, designerEmploymentType);
            setDesignerEquity(parseFloat(adjustedEquity.toFixed(2)));
        }
    };

    // Function to update EA periods and recalculate
    const updateEaPeriods = (newPeriods, hoursChanged = false) => {
        setEaPeriods(newPeriods);

        // If this is a hours change on the primary period, adjust the salary proportionally
        if (hoursChanged && newPeriods.length > 0 && eaType !== 'none') {
            const firstPeriod = newPeriods[0];
            // Get the ratio of new hours to full time (40 hours)
            const hourRatio = firstPeriod.hoursPerWeek / 40;

            // Scale the salary proportionally to the hours
            const maxSalary = eaType === 'regular'
                ? hiresConfig.ea.types.regular.maxSalary
                : hiresConfig.ea.types.clinical.maxSalary;

            // First check if salary is at or above the max
            const currentMaxSalary = maxSalary * hourRatio;
            if (eaSalary > currentMaxSalary) {
                // If salary would be above the new scaled max, adjust it down
                setEaSalary(Math.round(currentMaxSalary));
            }
        }

        // Recalculate equity based on new periods
        if (eaType !== 'none') {
            if (eaType === 'regular') {
                const baseEquity = hiresConfig.ea.types.regular.equityCalculation.formula(eaSalary);
                const adjustedEquity = calculateEffectiveEquityForPeriods(baseEquity, newPeriods, eaEmploymentType);
                setEaEquity(parseFloat(adjustedEquity.toFixed(2)));
            } else if (eaType === 'clinical') {
                const baseEquity = hiresConfig.ea.types.clinical.equityCalculation.formula(eaSalary);
                const adjustedEquity = calculateEffectiveEquityForPeriods(baseEquity, newPeriods, eaEmploymentType);
                setEaEquity(parseFloat(adjustedEquity.toFixed(2)));
            }
        }
    };

    // Function to update social media manager periods and recalculate
    const updateSocialPeriods = (newPeriods, hoursChanged = false) => {
        setSocialPeriods(newPeriods);

        // If this is a hours change on the primary period, adjust the salary proportionally
        if (hoursChanged && newPeriods.length > 0 && hireSocial) {
            const firstPeriod = newPeriods[0];
            // Get the ratio of new hours to full time (40 hours)
            const hourRatio = firstPeriod.hoursPerWeek / 40;

            // Scale the salary proportionally to the hours
            // First check if salary is at or above the max
            const currentMaxSalary = hiresConfig.social.maxSalary * hourRatio;
            if (socialSalary > currentMaxSalary) {
                // If salary would be above the new scaled max, adjust it down
                setSocialSalary(Math.round(currentMaxSalary));
            }
        }

        // Recalculate equity based on new periods
        if (hireSocial) {
            const baseEquity = hiresConfig.social.equityCalculation.formula(socialSalary);
            const adjustedEquity = calculateEffectiveEquityForPeriods(baseEquity, newPeriods, socialEmploymentType);
            setSocialEquity(parseFloat(adjustedEquity.toFixed(2)));
        }
    };

    return (
        <div className="mb-6">
            <h2 className="text-xl font-semibold mb-2">{hiresInfo.hiresLabel}</h2>

            {/* UI/UX Designer */}
            <div className="border p-4 rounded mb-4">
                <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center">
                        <input
                            type="checkbox"
                            checked={hireDesigner}
                            onChange={(e) => setHireDesigner(e.target.checked)}
                            className="mr-2"
                        />
                        <span
                            className="font-medium">{hiresConfig.designer.title} ({hiresConfig.designer.successImpact})</span>
                    </label>
                    <button
                        className="text-blue-500 text-sm"
                        onClick={() => setShowDesignerInfo(!showDesignerInfo)}
                    >
                        {showDesignerInfo ? hiresInfo.hideDetailsLabel : hiresInfo.showDetailsLabel}
                    </button>
                </div>
                {showDesignerInfo && hiresConfig.designer.description}

                {hireDesigner && (
                    <div className="pl-6">
                        {/* Employment Type Selection at Root Level */}
                        <div className="mb-3 mt-3">
                            <label className="block mb-1 font-medium">{hireOptionsConfig.employmentTypeLabel}</label>
                            <div className="flex gap-4">
                                <label className="flex items-center">
                                    <input
                                        type="radio"
                                        name="designerEmploymentType"
                                        value="employee"
                                        checked={designerEmploymentType === 'employee'}
                                        onChange={() => setDesignerEmploymentType('employee')}
                                        className="mr-2"
                                    />
                                    {hireOptionsConfig.employeeLabel}
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="radio"
                                        name="designerEmploymentType"
                                        value="contractor"
                                        checked={designerEmploymentType === 'contractor'}
                                        onChange={() => setDesignerEmploymentType('contractor')}
                                        className="mr-2"
                                    />
                                    {hireOptionsConfig.contractorLabel}
                                    {designerEmploymentType === 'contractor' && (
                                        <span className="text-orange-500 ml-1">{hireOptionsConfig.contractorCostNote || "(+50% cost, no equity)"}</span>
                                    )}
                                </label>
                            </div>
                        </div>

                        <div className="mb-3">
                            {/* Equity and Salary labels above the slider */}
                            <div className="flex justify-between mb-1">
                                <div className="text-sm font-medium">
                                    {hiresInfo.equityLabel}
                                    {designerEquity.toFixed(2)}%
                                </div>
                                <div className="text-sm font-medium">
                                    {hiresInfo.salaryLabel}
                                    {displaySalary(designerSalary, designerPeriods, designerEmploymentType)}
                                </div>
                            </div>

                            <input
                                type="range"
                                min="0"
                                max={hiresConfig.designer.maxSalary}
                                step="1000"
                                value={designerSalary}
                                onChange={(e) => setDesignerSalary(Number(e.target.value))}
                                className="w-full"
                            />
                            <div className="flex justify-between text-xs text-gray-500 mt-1">
                                <span>{formatCurrency(0)}</span>
                                <span>
                                    {formatCurrency(applyLocationMultiplier(hiresConfig.designer.maxSalary * (designerEmploymentType === 'contractor' ? 1.5 : 1)))}
                                    {designerEmploymentType === 'contractor' && (hiresInfo.contractorText ? ` (${hiresInfo.contractorText})` : ' (contractor)')}
                                    {designerPeriods && designerPeriods.length > 0 && designerPeriods[0].hoursPerWeek < 40 && ` (${Math.round(designerPeriods[0].hoursPerWeek/40*100)}${hiresInfo.percentSymbol || "%"})`}
                                </span>
                            </div>

                            <div className="text-xs text-gray-600 mt-1">
                                {designerEmploymentType === 'contractor' ?
                                    (hiresInfo.noEquityForContractorsText || 'No equity for contractors') :
                                    `${hiresInfo.equityCalculationText} ${hiresConfig.designer.equityCalculation.max}%
                                    ${hiresInfo.atLabel} ${formatCurrency(0)}, 0%
                                    ${hiresInfo.atLabel} ${formatCurrency(applyLocationMultiplier(hiresConfig.designer.maxSalary))}
                                    (${hiresInfo.currentLabel || "Current"}: ${designerEquity.toFixed(2)}% at ${formatCurrency(designerSalary)}
                                    ${designerPeriods && designerPeriods.length > 0 && designerPeriods[0].hoursPerWeek < 40 ?
                                    `${hiresInfo.withHoursText || "with"} ${Math.round(designerPeriods[0].hoursPerWeek/40*100)}% ${hiresInfo.hoursText || "hours"}` : ''})`
                                }
                            </div>
                        </div>

                        {/* Use HireMultiPeriodConfig with hideEmploymentType set to true */}
                        <HireMultiPeriodConfig
                            periods={designerPeriods}
                            setPeriods={updateDesignerPeriods}
                            configOptions={hireOptionsConfig}
                            updateSalaryCallback={updateDesignerPeriods}
                            hideEmploymentType={true}
                            employmentType={designerEmploymentType}
                            baseSalary={designerSalary}
                            formatCurrency={formatCurrency}
                            applyLocationMultiplier={applyLocationMultiplier}
                            multiPeriodConfigInfo={multiPeriodConfigInfo}
                            hireConfigPeriodInfo={hireConfigPeriodInfo}
                        />

                        {/* Using the HireCostSummary component */}
                        <HireCostSummary
                            salary={calculateEffectiveSalaryForPeriods(designerSalary, designerPeriods, designerEmploymentType)}
                            equity={designerEquity}
                            successModifier={hiresConfig.designer.successImpact}
                            formatCurrency={formatCurrency}
                            location={location}
                            applyLocationMultiplier={applyLocationMultiplier}
                            hoursPerWeek={calculateAverageHours(designerPeriods)}
                            workConfiguration={displayWorkConfiguration(designerPeriods)}
                            employmentType={designerEmploymentType}
                            hireCostSummaryInfo={hireCostSummaryInfo}
                            summaryBoxInfo={summaryBoxInfo}
                        />
                    </div>
                )}
            </div>

            {/* Executive Assistant */}
            <div className="border p-4 rounded mb-4">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium">{hiresConfig.ea.title} ({hiresConfig.ea.successImpact})</h3>
                    <button
                        className="text-blue-500 text-sm"
                        onClick={() => setShowEAInfo(!showEAInfo)}
                    >
                        {showEAInfo ? hiresInfo.hideDetailsLabel : hiresInfo.showDetailsLabel}
                    </button>
                </div>

                {showEAInfo && hiresConfig.ea.description}

                <div className="mb-2">
                    <label className="flex items-center">
                        <input
                            type="radio"
                            name="eaType"
                            value="none"
                            checked={eaType === 'none'}
                            onChange={() => setEaType('none')}
                            className="mr-2"
                        />
                        {hiresInfo.noneLabel}
                    </label>
                </div>

                <div className="mb-2">
                    <label className="flex items-center">
                        <input
                            type="radio"
                            name="eaType"
                            value="regular"
                            checked={eaType === 'regular'}
                            onChange={() => {
                                setEaType('regular');
                                setEaSalary(30000);
                            }}
                            className="mr-2"
                        />
                        {hiresConfig.ea.types.regular.title} ({hiresConfig.ea.types.regular.successImpact})
                    </label>
                </div>

                <div className="mb-2">
                    <label className="flex items-center">
                        <input
                            type="radio"
                            name="eaType"
                            value="clinical"
                            checked={eaType === 'clinical'}
                            onChange={() => {
                                setEaType('clinical');
                                setEaSalary(80000);
                            }}
                            className="mr-2"
                        />
                        {hiresConfig.ea.types.clinical.title} ({hiresConfig.ea.types.clinical.successImpact})
                    </label>
                </div>

                {eaType !== 'none' && (
                    <div className="pl-6 mt-3">
                        {/* Employment Type Selection at Root Level */}
                        <div className="mb-3 mt-3">
                            <label className="block mb-1 font-medium">{hireOptionsConfig.employmentTypeLabel}</label>
                            <div className="flex gap-4">
                                <label className="flex items-center">
                                    <input
                                        type="radio"
                                        name="eaEmploymentType"
                                        value="employee"
                                        checked={eaEmploymentType === 'employee'}
                                        onChange={() => setEaEmploymentType('employee')}
                                        className="mr-2"
                                    />
                                    {hireOptionsConfig.employeeLabel}
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="radio"
                                        name="eaEmploymentType"
                                        value="contractor"
                                        checked={eaEmploymentType === 'contractor'}
                                        onChange={() => setEaEmploymentType('contractor')}
                                        className="mr-2"
                                    />
                                    {hireOptionsConfig.contractorLabel}
                                    {eaEmploymentType === 'contractor' && (
                                        <span className="text-orange-500 ml-1">{hireOptionsConfig.contractorCostNote || "(+50% cost, no equity)"}</span>
                                    )}
                                </label>
                            </div>
                        </div>

                        <div className="mb-3">
                            {/* Equity and Salary labels above the slider */}
                            <div className="flex justify-between mb-1">
                                <div className="text-sm font-medium">
                                    {hiresInfo.equityLabel}
                                    {eaEquity.toFixed(2)}%
                                </div>
                                <div className="text-sm font-medium">
                                    {hiresInfo.salaryLabel}
                                    {displaySalary(eaSalary, eaPeriods, eaEmploymentType)}
                                </div>
                            </div>

                            <input
                                type="range"
                                min="0"
                                max={(eaType === 'regular'
                                    ? hiresConfig.ea.types.regular.maxSalary
                                    : hiresConfig.ea.types.clinical.maxSalary)}
                                step="1000"
                                value={eaSalary}
                                onChange={(e) => setEaSalary(Number(e.target.value))}
                                className="w-full"
                            />
                            <div className="flex justify-between text-xs text-gray-500 mt-1">
                                <span>{formatCurrency(0)}</span>
                                <span>
                                    {formatCurrency(applyLocationMultiplier((eaType === 'regular'
                                        ? hiresConfig.ea.types.regular.maxSalary
                                        : hiresConfig.ea.types.clinical.maxSalary) * (eaEmploymentType === 'contractor' ? 1.5 : 1)))}
                                    {eaEmploymentType === 'contractor' && (hiresInfo.contractorText ? ` (${hiresInfo.contractorText})` : ' (contractor)')}
                                    {eaPeriods && eaPeriods.length > 0 && eaPeriods[0].hoursPerWeek < 40 && ` (${Math.round(eaPeriods[0].hoursPerWeek/40*100)}${hiresInfo.percentSymbol || "%"})`}
                                </span>
                            </div>

                            <div className="text-xs text-gray-600 mt-1">
                                {eaEmploymentType === 'contractor' ?
                                    (hiresInfo.noEquityForContractorsText || 'No equity for contractors') :
                                    `${hiresInfo.equityCalculationText} ${eaType === 'regular'
                                        ? hiresConfig.ea.types.regular.equityCalculation.max
                                        : hiresConfig.ea.types.clinical.equityCalculation.max}% ${hiresInfo.atLabel} ${formatCurrency(0)}, 0% ${hiresInfo.atLabel} ${formatCurrency(applyLocationMultiplier(eaType === 'regular'
                                        ? hiresConfig.ea.types.regular.maxSalary
                                        : hiresConfig.ea.types.clinical.maxSalary))}
                                    (${hiresInfo.currentLabel || "Current"}: ${eaEquity.toFixed(2)}% at ${formatCurrency(eaSalary)}
                                    ${eaPeriods && eaPeriods.length > 0 && eaPeriods[0].hoursPerWeek < 40 ?
                                    `${hiresInfo.withHoursText || "with"} ${Math.round(eaPeriods[0].hoursPerWeek/40*100)}% ${hiresInfo.hoursText || "hours"}` : ''})`
                                }
                            </div>
                        </div>

                        {/* Use HireMultiPeriodConfig with hideEmploymentType set to true */}
                        <HireMultiPeriodConfig
                            periods={eaPeriods}
                            setPeriods={updateEaPeriods}
                            configOptions={hireOptionsConfig}
                            updateSalaryCallback={updateEaPeriods}
                            hideEmploymentType={true}
                            employmentType={eaEmploymentType}
                            baseSalary={eaSalary}
                            formatCurrency={formatCurrency}
                            applyLocationMultiplier={applyLocationMultiplier}
                            multiPeriodConfigInfo={multiPeriodConfigInfo}
                            hireConfigPeriodInfo={hireConfigPeriodInfo}
                        />

                        <div className="mb-3 text-sm text-green-600">
                            {hiresConfig.ea.savingsDescription}
                        </div>

                        {/* Using the HireCostSummary component */}
                        <HireCostSummary
                            salary={calculateEffectiveSalaryForPeriods(eaSalary, eaPeriods, eaEmploymentType)}
                            equity={eaEquity}
                            successModifier="" // This will be handled by the isEA and eaType props
                            formatCurrency={formatCurrency}
                            isEA={true}
                            eaType={eaType}
                            tristenSavings={calculateEffectiveSalaryForPeriods(hiresConfig.ea.tristenSavings, eaPeriods, 'employee')}
                            location={location}
                            applyLocationMultiplier={applyLocationMultiplier}
                            hoursPerWeek={calculateAverageHours(eaPeriods)}
                            workConfiguration={displayWorkConfiguration(eaPeriods)}
                            employmentType={eaEmploymentType}
                            hireCostSummaryInfo={hireCostSummaryInfo}
                            summaryBoxInfo={summaryBoxInfo}
                        />
                    </div>
                )}
            </div>

            {/* Social Media Manager */}
            <div className="border p-4 rounded mb-4">
                <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center">
                        <input
                            type="checkbox"
                            checked={hireSocial}
                            onChange={(e) => setHireSocial(e.target.checked)}
                            className="mr-2"
                        />
                        <span
                            className="font-medium">{hiresConfig.social.title} ({hiresConfig.social.successImpact})</span>
                    </label>
                    <button
                        className="text-blue-500 text-sm"
                        onClick={() => setShowSocialInfo(!showSocialInfo)}
                    >
                        {showSocialInfo ? hiresInfo.hideDetailsLabel : hiresInfo.showDetailsLabel}
                    </button>
                </div>

                {showSocialInfo && hiresConfig.social.description}

                {hireSocial && (
                    <div className="pl-6">
                        {/* Employment Type Selection at Root Level */}
                        <div className="mb-3 mt-3">
                            <label className="block mb-1 font-medium">{hireOptionsConfig.employmentTypeLabel}</label>
                            <div className="flex gap-4">
                                <label className="flex items-center">
                                    <input
                                        type="radio"
                                        name="socialEmploymentType"
                                        value="employee"
                                        checked={socialEmploymentType === 'employee'}
                                        onChange={() => setSocialEmploymentType('employee')}
                                        className="mr-2"
                                    />
                                    {hireOptionsConfig.employeeLabel}
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="radio"
                                        name="socialEmploymentType"
                                        value="contractor"
                                        checked={socialEmploymentType === 'contractor'}
                                        onChange={() => setSocialEmploymentType('contractor')}
                                        className="mr-2"
                                    />
                                    {hireOptionsConfig.contractorLabel}
                                    {socialEmploymentType === 'contractor' && (
                                        <span className="text-orange-500 ml-1">{hireOptionsConfig.contractorCostNote || "(+50% cost, no equity)"}</span>
                                    )}
                                </label>
                            </div>
                        </div>

                        <div className="mb-3">
                            {/* Equity and Salary labels above the slider */}
                            <div className="flex justify-between mb-1">
                                <div className="text-sm font-medium">
                                    {hiresInfo.equityLabel}
                                    {socialEquity.toFixed(2)}%
                                </div>
                                <div className="text-sm font-medium">
                                    {hiresInfo.salaryLabel}
                                    {displaySalary(socialSalary, socialPeriods, socialEmploymentType)}
                                </div>
                            </div>

                            <input
                                type="range"
                                min="0"
                                max={hiresConfig.social.maxSalary}
                                step="1000"
                                value={socialSalary}
                                onChange={(e) => setSocialSalary(Number(e.target.value))}
                                className="w-full"
                            />
                            <div className="flex justify-between text-xs text-gray-500 mt-1">
                                <span>{formatCurrency(0)}</span>
                                <span>
                                    {formatCurrency(applyLocationMultiplier(hiresConfig.social.maxSalary * (socialEmploymentType === 'contractor' ? 1.5 : 1)))}
                                    {socialEmploymentType === 'contractor' && (hiresInfo.contractorText ? ` (${hiresInfo.contractorText})` : ' (contractor)')}
                                    {socialPeriods && socialPeriods.length > 0 && socialPeriods[0].hoursPerWeek < 40 && ` (${Math.round(socialPeriods[0].hoursPerWeek/40*100)}${hiresInfo.percentSymbol || "%"})`}
                                </span>
                            </div>

                            <div className="text-xs text-gray-600 mt-1">
                                {socialEmploymentType === 'contractor' ?
                                    (hiresInfo.noEquityForContractorsText || 'No equity for contractors') :
                                    `${hiresInfo.equityCalculationText} ${hiresConfig.social.equityCalculation.max}%
                                    ${hiresInfo.atLabel} ${formatCurrency(0)}, 0%
                                    ${hiresInfo.atLabel} ${formatCurrency(applyLocationMultiplier(hiresConfig.social.maxSalary))}
                                    (${hiresInfo.currentLabel || "Current"}: ${socialEquity.toFixed(2)}% at ${formatCurrency(socialSalary)}
                                    ${socialPeriods && socialPeriods.length > 0 && socialPeriods[0].hoursPerWeek < 40 ?
                                    `${hiresInfo.withHoursText || "with"} ${Math.round(socialPeriods[0].hoursPerWeek/40*100)}% ${hiresInfo.hoursText || "hours"}` : ''})`
                                }
                            </div>
                        </div>

                        {/* Use HireMultiPeriodConfig with hideEmploymentType set to true */}
                        <HireMultiPeriodConfig
                            periods={socialPeriods}
                            setPeriods={updateSocialPeriods}
                            configOptions={hireOptionsConfig}
                            updateSalaryCallback={updateSocialPeriods}
                            hideEmploymentType={true}
                            employmentType={socialEmploymentType}
                            baseSalary={socialSalary}
                            formatCurrency={formatCurrency}
                            applyLocationMultiplier={applyLocationMultiplier}
                            multiPeriodConfigInfo={multiPeriodConfigInfo}
                            hireConfigPeriodInfo={hireConfigPeriodInfo}
                        />

                        {/* Using the HireCostSummary component */}
                        <HireCostSummary
                            salary={calculateEffectiveSalaryForPeriods(socialSalary, socialPeriods, socialEmploymentType)}
                            equity={socialEquity}
                            successModifier={hiresConfig.social.successImpact}
                            formatCurrency={formatCurrency}
                            location={location}
                            applyLocationMultiplier={applyLocationMultiplier}
                            hoursPerWeek={calculateAverageHours(socialPeriods)}
                            workConfiguration={displayWorkConfiguration(socialPeriods)}
                            employmentType={socialEmploymentType}
                            hireCostSummaryInfo={hireCostSummaryInfo}
                            summaryBoxInfo={summaryBoxInfo}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default Hires;